/**
 * Ktor-based API handler for safe HTTP calls in Kotlin/JS.
 *
 * Wraps every Ktor request in a try/catch and maps results to [APIState].
 * Retrofit and java.io.IOException are JVM-only — replaced with Ktor's
 * [ResponseException] and Kotlin's [Exception].
 */
package org.moxultimate.topwithmox.core.handler

import io.ktor.client.call.*
import io.ktor.client.plugins.*
import io.ktor.client.statement.*
import io.ktor.http.*
import org.moxultimate.topwithmox.core.state.APIState

/**
 * Stateless helper that executes a suspend Ktor call and wraps the result
 * in [APIState.Success] or [APIState.Error].
 *
 * Usage:
 * ```kotlin
 * val state = APIHandler.safeApiCall { client.get("https://api.example.com/users") }
 * val stateTransformed = APIHandler.safeApiCall(
 *     apiToBeCalled = { client.get("https://api.example.com/users") },
 *     transformer    = { response: List<UserDto> -> response.map { it.toDomain() } }
 * )
 * ```
 */
object APIHandler {

    /**
     * Executes [apiToBeCalled] and returns [APIState.Success] with the typed body,
     * or [APIState.Error] with a human-readable message on failure.
     *
     * @param T expected response body type (must be deserializable by Ktor's ContentNegotiation).
     * @param apiToBeCalled suspend lambda that performs the Ktor request and returns [HttpResponse].
     */
    suspend inline fun <reified T> safeApiCall(
        crossinline apiToBeCalled: suspend () -> HttpResponse,
    ): APIState<T> {
        return try {
            val response = apiToBeCalled()
            if (!response.status.isSuccess()) {
                val code = response.status.value
                return APIState.Error(handleErrorMessage(code, response.bodyAsText()), code)
            }
            APIState.Success(data = response.body<T>())
        } catch (e: ResponseException) {
            val code = e.response.status.value
            APIState.Error(handleErrorMessage(code, e.message.orEmpty()), code)
        } catch (e: Throwable) {
            APIState.Error(handleErrorMessage(message = e.message.orEmpty()))
        }
    }

    /**
     * Executes [apiToBeCalled], applies [transformer] on success, and returns
     * [APIState.Success<R>] or [APIState.Error].
     *
     * Use this overload when the API DTO needs to be mapped to a domain model
     * before the result reaches the ViewModel.
     *
     * @param T raw response body type from the API.
     * @param R domain type returned to callers.
     * @param apiToBeCalled suspend lambda that performs the Ktor request and returns [HttpResponse].
     * @param transformer   pure function that converts [T] to [R].
     */
    suspend inline fun <reified T, R> safeApiCall(
        crossinline apiToBeCalled: suspend () -> HttpResponse,
        crossinline transformer: (T) -> R,
    ): APIState<R> {
        return try {
            val response = apiToBeCalled()
            if (!response.status.isSuccess()) {
                val code = response.status.value
                return APIState.Error(handleErrorMessage(code, response.bodyAsText()), code)
            }
            val body = response.body<T>()
            APIState.Success(data = transformer(body))
        } catch (e: ResponseException) {
            val code = e.response.status.value
            APIState.Error(handleErrorMessage(code, e.message.orEmpty()), code)
        } catch (e: Throwable) {
            APIState.Error(handleErrorMessage(message = e.message.orEmpty()))
        }
    }

    /**
     * Maps an HTTP status code and raw [message] to a user-friendly error string.
     *
     * Extend the `when` block with project-specific status codes as needed.
     *
     * @param statusCode HTTP status code, or 0 when no response was received.
     * @param message    raw exception or response message.
     */
    fun handleErrorMessage(statusCode: Int = 0, message: String): String {
        if (message.contains("Failed to fetch", ignoreCase = true) ||
            message.contains("NetworkError", ignoreCase = true) ||
            message.contains("ECONNREFUSED", ignoreCase = true) ||
            message.contains("ERR_CONNECTION_REFUSED", ignoreCase = true)
        ) {
            return "No internet connection or the server is unreachable"
        }

        return when (statusCode) {
            401  -> "Unauthorized — check credentials or session"
            403  -> "Forbidden — you don't have permission"
            404  -> "API endpoint not found"
            409  -> "Conflict — resource already exists"
            422  -> "Validation error — check request data"
            500  -> "Server error (500): ${extractDetail(message)}"
            503  -> "Service unavailable — server is down"
            else -> message.ifBlank { "An unexpected error occurred" }
        }
    }

    /**
     * extractDetail
     * Extracts error info from the backend JSON body.
     * Checks "detail" (root cause) first, then "message" (generic), then falls back to raw text.
     * If "type" is present it is prepended as [TypeName] for easier diagnosis.
     */
    private fun extractDetail(raw: String): String {
        if (raw.isBlank()) return "try again later"
        val detail = Regex(""""detail"\s*:\s*"([^"]+)"""").find(raw)?.groupValues?.getOrNull(1)?.ifBlank { null }
        val type   = Regex(""""type"\s*:\s*"([^"]+)"""").find(raw)?.groupValues?.getOrNull(1)?.ifBlank { null }
        if (detail != null) return if (type != null) "[$type] $detail" else detail
        val message = Regex(""""message"\s*:\s*"([^"]+)"""").find(raw)?.groupValues?.getOrNull(1)?.ifBlank { null }
        return message ?: raw.trim().take(400)
    }
}
